import React, { useEffect, useState } from 'react';
import { CONFIG } from "../config"; // Ensure CONFIG has API_BASE_URL and TRUCK_HEALTH_OVERVIEW_URL
import { Link } from 'react-router-dom';
import CustomDropdown from './CustomDropdown';
import posthog from 'posthog-js';
import SearchWithSuggestionsPortal from './SearchWithSuggestionsPortal';


const TruckHealthOverview = ({ selectedDate, selectedStatus, selectedAgeRange, selectedCompliance, selectedFailedComponent }) => {

  const DEFAULT_STATUS = "Filter By Status";
  const DEFAULT_ISSUE = "Filter By Recent Issue";
  const DEFAULT_MAKE = "Filter By Make";
  const DEFAULT_MODEL = "Filter By Model";

  const [statuses, setStatuses] = useState([]);
  const [selectedFilterStatus, setSelectedFilterStatus] = useState('');
  const [makes, setMakes] = useState([]);
  const [selectedMake, setSelectedMake] = useState('');
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [issues, setIssues] = useState([]);
  const [selectedIssue, setSelectedIssue] = useState('');

  const [loading, setLoading] = useState(false);
  const [trucks, setTrucks] = useState([]);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const columns = ["vehicle_number", "status", "make", "model", "top_issue", "age"];
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const restrictedIds = JSON.parse(process.env.REACT_APP_RESTRICTED_USER_ID || '[]');
  const restrictedDevice = process.env.REACT_APP_RESTRICTED_DEVICE || '';
  const deviceProvider = localStorage.getItem("tensor-user-device-provider");
  const userId = parseInt(localStorage.getItem("tensor-user-id"), 10);
  //const isRestricted = deviceProvider === restrictedDevice;
  const isRestricted = deviceProvider === restrictedDevice;

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.trim() === "") {
      setSuggestions([]);
      setSelectedFilter("");
      return;
    }
    const generatedSuggestions = columns.map((col) => `${col}:${value}`);
    setSuggestions(generatedSuggestions);
  };

  const desanitizeStatus = (status) => {
    if (!status || typeof status !== 'string') return '';
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Fetch truck health data whenever the selectedDate changes.
  useEffect(() => {
    if (!selectedDate) return;
    setSelectedFilterStatus(desanitizeStatus(selectedStatus));
    const fetchTruckData = async () => {
      setLoading(true);
      try {
        const selectedAgeRangeFix =
          selectedAgeRange && selectedAgeRange.startsWith("0-")
            ? selectedAgeRange
            : selectedAgeRange
              ? selectedAgeRange.replace(/^(\d+)-/, (_, start) => `${parseInt(start) + 1}-`)
              : null;

        const selectedComplianceFix = selectedCompliance
          ? selectedCompliance
            .replace(" (Non Compliant)", ":non_compliant")
            .replace(" (Compliant)", ":compliant")
          : null;

        const selectedFailedComponentFix = selectedFailedComponent
          ? selectedFailedComponent
            .replace(" (Last 6 weeks)", ":last6")
            .replace(" (Next 4 weeks)", ":next4")
          : null;

        const response = await fetch(
          `${CONFIG.API_BASE_URL}/${CONFIG.TRUCK_HEALTH_OVERVIEW_URL}/${localStorage.getItem(
            "tensor-user-id"
          )}?date_str=${selectedDate}&selected_status=${selectedStatus}&selected_agerange=${selectedAgeRangeFix}&selected_compliance=${selectedComplianceFix}&selected_filter=${selectedFilter}&selected_top_failure=${selectedFailedComponentFix}`,
          {
            headers: {
              "Content-Type": "application/json",
              "Authorization": "Bearer " + localStorage.getItem("tensor-auth-token"),
            },
          }
        );
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          setTrucks(data.data);
          console.dir(data.data);
          setError(null);
        } else {
          setTrucks([]);
          setError("No truck data available.");
        }
      } catch (err) {
        console.error("Failed to fetch truck data:", err);
        setError("Failed to fetch truck data.");
        setTrucks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTruckData();
  }, [selectedDate, selectedStatus, selectedAgeRange, selectedCompliance, selectedFailedComponent, selectedFilter]);

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const response = await fetch(`${CONFIG.API_BASE_URL}/${CONFIG.TRUCK_HEALTH_OVERVIEW_FILTERS}/${localStorage.getItem(
          "tensor-user-id"
        )}`, {
          headers: {
            "Authorization": "Bearer " + localStorage.getItem("tensor-auth-token"),
          }
        });
        const data = await response.json();
        if (data) {
          setStatuses([DEFAULT_STATUS, ...(data.data.vehicle_status || [])]);
          setIssues([DEFAULT_ISSUE, ...(data.data.vehicle_issue || [])]);
          setMakes([DEFAULT_MAKE, ...(data.data.vehicle_make || [])]);
          setModels([DEFAULT_MODEL, ...(data.data.vehicle_model || [])]);
        }
      } catch (error) {
        console.error("Error fetching filter data:", error);
      }
    };

    fetchFilterData();
  }, []);

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? '▲' : '▼';
    }
    return '⇅';
  };

  const onSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    const sortedData = [...trucks].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setTrucks(sortedData);
  };

  const getComplianceColor = (value) => {
    const numericValue = typeof value === "string" ? parseFloat(value.replace("%", "")) : value;

    if (numericValue === 100) return "text-green-600";
    if (numericValue >= 90 && numericValue <= 99) return "table_warning_bg_color";
    return "text-red-600";
  };

  const getAgeColor = (value) => {
    if (value <= 5) return "text-green-600  font-normal";
    if (value > 5 && value <= 9) return "table_warning_bg_color font-semibold";
    return "text-red-600 font-bold";
  };

  const getMaintenanceColor = (value) => {
    if (value > 90) return "text-red-600 font-bold";
    return "text-green-600 font-normal";
  };

  const getDowntimeNextWeekColor = (value) => {
    if (value > 0) return "text-red-600 font-bold";
    return "text-green-600 font-normal";
  };

  return (
    <section className="pt-2 relative z-[9999]">
      {loading && (
        <div class="text-center">
          <div class="flex justify-center mb-4">
            <svg class="w-6 h-6 text-blue-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z">
              </path>
            </svg>
          </div>
        </div>
      )}

      {!loading && (
        <div>
          <h2 className="text-xl font-semibold mb-4 text-2xl font-bold truck_page_head_text">Truck Health Overview</h2>
          <div className="flex flex-wrap gap-x-2 gap-y-4 mb-6 relative overflow-visible z-0">

            <CustomDropdown
              label="Filter By Status"
              options={statuses}
              selected={selectedFilterStatus}
              setSelected={setSelectedFilterStatus}
            />
            <CustomDropdown
              label="Filter By Make"
              options={makes}
              selected={selectedMake}
              setSelected={setSelectedMake}
            />
            <CustomDropdown
              label="Filter By Model"
              options={models}
              selected={selectedModel}
              setSelected={setSelectedModel}
            />
            <CustomDropdown
              label="Filter By Recent Issue"
              options={issues}
              selected={selectedIssue}
              setSelected={setSelectedIssue}
            />
            <SearchWithSuggestionsPortal
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              suggestions={suggestions}
              onSelectSuggestion={(item) => {
                setSelectedFilter(item);
                setSuggestions([]);
              }}
            />




          </div>

          <div className="overflow-x-auto bg-white shadow-sm truck_table_border p-4 relative z-0">
            <div className="max-h-[580px] overflow-y-visible">

              <table className="min-w-full text-sm text-left truck_table_bg">
                <thead className="text-gray-700 sticky top-0 bg-white z-10">
                  <tr>
                    <th className="px-2 py-4 truck_table_col cursor-pointer" onClick={() => onSort('vehicle_number')}>
                      Vehicle No. <span>{getSortIcon('vehicle_number')}</span>
                    </th>
                    <th className="px-2 py-4 truck_table_col cursor-pointer" onClick={() => onSort('make')}>Make <span>{getSortIcon('make')}</span></th>
                    <th className="px-2 py-4 truck_table_col cursor-pointer" onClick={() => onSort('model')}>Model <span>{getSortIcon('model')}</span></th>
                    <th className="px-2 py-4 truck_table_col cursor-pointer" onClick={() => onSort('manufacturer_year')}>Manufactured year <span>{getSortIcon('manufacturer_year')}</span></th>
                    <th className="px-2 py-4 truck_table_col cursor-pointer" onClick={() => onSort('status')}>Status <span>{getSortIcon('status')}</span></th>
                    <th className="px-2 py-4 truck_table_col whitespace-nowrap cursor-pointer" onClick={() => onSort('status')}>Age <br />(yrs) <span>{getSortIcon('age')}</span></th>
                    <th className="px-4 py-4 truck_table_col cursor-pointer" onClick={() => onSort('mileage')}>Miles <span>{getSortIcon('mileage')}</span></th>
                    <th className="px-2 py-4 truck_table_col cursor-pointer" onClick={() => onSort('last_maintenance_days_ago')}>Last maintenance (days ago) <span>{getSortIcon('last_maintenance_days_ago')}</span></th>
                    <th className="px-2 py-4 truck_table_col cursor-pointer" onClick={() => onSort('downtime_next_week_days')}>Downtime next week (days) <span>{getSortIcon('downtime_next_week_days')}</span></th>
                    <th className="px-2 py-4 truck_table_col whitespace-nowrap cursor-pointer" onClick={() => onSort('maintenance_compliance')}>Maintenance <br />compliance <span>{getSortIcon('maintenance_compliance')}</span></th>
                    <th className="px-2 py-4 truck_table_col whitespace-nowrap cursor-pointer" onClick={() => onSort('inspection_compliance')}>Inspection <br />compliance <span>{getSortIcon('inspection_compliance')}</span></th>
                    <th className="px-2 py-4 truck_table_col whitespace-nowrap cursor-pointer" onClick={() => onSort('dvir_compliance')}>DVIR <br />compliance <span>{getSortIcon('dvir_compliance')}</span></th>
                    <th className="px-2 py-4 truck_table_col whitespace-nowrap cursor-pointer" onClick={() => onSort('avg_cost_per_mile')}>Avg. Cost <br />per Mile ($) <span>{getSortIcon('avg_cost_per_mile')}</span></th>
                    <th className="px-2 py-4 truck_table_col cursor-pointer" onClick={() => onSort('monthly_maintenance_cost')}>Monthly maintenance Cost ($) <span>{getSortIcon('monthly_maintenance_cost')}</span></th>
                    <th className="px-2 py-4 truck_table_col cursor-pointer" onClick={() => onSort('top_issue')}>Recent issue <span>{getSortIcon('top_issue')}</span></th>
                  </tr>
                </thead>
                <tbody>
                  {trucks.filter(truck => {
                    const matchIssue =
                      !selectedIssue || selectedIssue === DEFAULT_ISSUE
                        ? true
                        : truck.top_issue?.toLowerCase() === selectedIssue.toLowerCase();

                    const matchStatus =
                      !selectedFilterStatus || selectedFilterStatus === DEFAULT_STATUS
                        ? true
                        : truck.status?.toLowerCase() === selectedFilterStatus.toLowerCase();

                    const matchMake =
                      !selectedMake || selectedMake === DEFAULT_MAKE
                        ? true
                        : truck.make?.toLowerCase() === selectedMake.toLowerCase();

                    const matchModel =
                      !selectedModel || selectedModel === DEFAULT_MODEL
                        ? true
                        : truck.model?.toLowerCase() === selectedModel.toLowerCase();

                    return matchIssue && matchStatus && matchMake && matchModel;
                  }).length === 0 && (
                      <tr>
                        <td colSpan="15" className="text-center py-4 text-gray-500">
                          No trucks found for selected issue.
                        </td>
                      </tr>
                    )}
                  {trucks.filter((truck) => {
                    const matchIssue =
                      !selectedIssue || selectedIssue === DEFAULT_ISSUE
                        ? true
                        : truck.top_issue?.toLowerCase() === selectedIssue.toLowerCase();

                    const matchStatus =
                      !selectedFilterStatus || selectedFilterStatus === DEFAULT_STATUS
                        ? true
                        : truck.status?.toLowerCase() === selectedFilterStatus.toLowerCase();

                    const matchMake =
                      !selectedMake || selectedMake === DEFAULT_MAKE
                        ? true
                        : truck.make?.toLowerCase() === selectedMake.toLowerCase();

                    const matchModel =
                      !selectedModel || selectedModel === DEFAULT_MODEL
                        ? true
                        : truck.model?.toLowerCase() === selectedModel.toLowerCase();

                    return matchIssue && matchStatus && matchMake && matchModel;
                  })
                    .map((truck) => (
                      <tr key={truck.id} className="hover:bg-gray-50 border-2 border-gray-200">
                        <td className="px-2 py-4 text-blue-600 font-medium hover:underline cursor-pointer">
                          <Link
                            to={`/vehicle/${truck.vehicle_id}`}
                            className="text-blue-600 hover:underline cursor-pointer underline"
                            onClick={() => {
                              try {
                                posthog.capture("showing_vehicle_health_for_vehicle", {
                                  vehicle_number: truck.vehicle_number,
                                  vehicle_id: truck.vehicle_id,
                                  user_id: userId,
                                });
                              } catch (err) {
                                console.warn("PostHog event failed:", err);
                              }
                            }}
                          >
                            {truck.vehicle_number}
                          </Link>
                        </td>
                        <td className="px-2 py-2 table_cells_text t_text_color">{truck.make}</td>
                        <td className="px-2 py-2 table_cells_text t_text_color">{truck.model}</td>
                        <td className="px-4 py-2 table_cells_text t_text_color">{truck.manufacturer_year}</td>
                        <td className={`px-2 py-2 font-medium whitespace-nowrap ${truck.status.toLowerCase() === "healthy"
                          ? "text-green-600"
                          : truck.status.toLowerCase() === "at risk"
                            ? "table_warning_bg_color"
                            : "text-red-600"
                          }`}>
                          {truck.status.toLowerCase() === "healthy" ? (
                            <span>
                              <i className="ti ti-circle-check" />
                            </span>
                          ) : truck.status.toLowerCase() === "at risk" ? (
                            <span>
                              <i className="ti ti-alert-triangle" />
                            </span>
                          ) : (
                            <span>
                              <i className="ti ti-circle-x" />
                            </span>
                          )}
                          <span className="font-semibold">&nbsp;{truck.status}</span>
                        </td>
                        <td className={`px-4 py-2 table_cells_text_normal ${getAgeColor(truck.age)}`}>
                          {truck.age}
                        </td>
                        <td className="px-4 py-2 table_cells_text t_text_color">
                          {truck?.mileage != null ? `${new Intl.NumberFormat().format(truck.mileage)}` : 'NA'}
                        </td>
                        <td className={`px-10 py-2 table_cells_text_normal ${getMaintenanceColor(truck.last_maintenance_days_ago)}`}>
                          {truck.last_maintenance_days_ago}
                        </td>
                        <td className={`px-4 py-2 table_cells_text_normal ${getDowntimeNextWeekColor(truck.downtime_next_week_days)}`}>
                          {truck.downtime_next_week_days > 0 && <span>{truck.downtime_next_week_days}</span>}
                        </td>
                        <td className={`px-2 py-2 table_cells_text_normal font-medium ${getComplianceColor(truck.maintenance_compliance)} font-semibold`}>
                          {!isRestricted ? truck.maintenance_compliance : null}
                        </td>
                        <td className={`px-2 py-2 table_cells_text_normal ${getComplianceColor(truck.inspection_compliance)} font-semibold`}>
                          {!isRestricted ? truck.inspection_compliance : null}
                        </td>
                        <td className={`px-2 py-2 table_cells_text_normal ${getComplianceColor(truck.dvir_compliance)} font-semibold`}>
                          {!isRestricted ? truck.dvir_compliance : ''}
                        </td>
                        <td className={`px-2 py-2 table_cells_text t_text_color`}>
                          {truck.avg_cost_per_mile > 0 && <span>{truck.avg_cost_per_mile}</span>}
                        </td>
                        <td className={`px-4 py-2 table_cells_text t_text_color`}>
                          {truck?.monthly_maintenance_cost != null ? `${new Intl.NumberFormat().format(truck.monthly_maintenance_cost)}` : 'NA'}
                        </td>
                        <td className={`px-4 py-2 table_cells_text t_text_color`}>
                          {truck.top_issue}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default TruckHealthOverview;
